import { CSSProperties, useCallback } from 'react';
import { Paper, Stack, Title, Button, Group, Input, Transition } from '@mantine/core';
import Text from '@ui/components/Text/Text';
import Callout from '@ui/components/Callout/Callout';

import Workspace from 'src/entities/workspace';
import useEditWorkspaceName from './useEditWorkspaceName';

const EditWorkspaceName = ( {
	workspace, showCustomBrandingCallout = false, readOnly = false
}: { workspace: Workspace; showCustomBrandingCallout?: boolean; readOnly?: boolean; } ) => {
	const {
		name, onInputChange, onSaveClick, saveButtonDisabled, isSaving
	} = useEditWorkspaceName( { workspace, readOnly } );

	const submitOnEnter = useCallback( ( event: React.KeyboardEvent<HTMLInputElement> ) => {
		if ( event.key === 'Enter' && !saveButtonDisabled ) {
			onSaveClick();
		}
	}, [ onSaveClick, saveButtonDisabled ] );

	return (
		<Paper p={20} shadow="xs" style={{ background: 'var(--color-bg-container)' }}>
			<Stack spacing={20}>
				<Stack spacing={14}>
					<Title order={4}>Workspace Name</Title>
					<Text style={{ color: 'var(--color-text-primary)' }}>Manage your workspace&apos;s name.</Text>
				</Stack>
				<Group spacing={6}>
					<Input
						value={name}
						onChange={onInputChange}
						disabled={readOnly}
						style={{ flex: 1 }}
						onKeyDown={submitOnEnter}
						styles={{ input: { background: 'var(--color-bg-container)' } }}
					/>
					<Button disabled={saveButtonDisabled} onClick={onSaveClick} loading={isSaving}>
						Save
					</Button>
				</Group>
				<Transition mounted={showCustomBrandingCallout} transition="fade" duration={400} timingFunction="ease">
					{( styles: CSSProperties ) => (
						<Callout
							style={styles}
							type="promotional"
							variant="subtle"
							withIcon={false}
							text="Once you customize your logo, your workspace name will appear in
								email subjects (e.g., &quot;[Workspace Name] via VeryBusy&quot;)."
						/>
					)}
				</Transition>
			</Stack>
		</Paper>
	);
};

export default EditWorkspaceName;
